<script setup lang="ts">
/**
 * ConfirmModal — Reusable confirmation dialog
 *
 * Replaces browser confirm() with a professional modal.
 * Wraps BaseModal with destructive-action styling.
 *
 * Keyboard shortcuts:
 * - Enter / Space → Confirm
 * - Escape → Cancel (also handled by BaseModal)
 */
import { watch, onUnmounted } from 'vue'
import BaseModal from './BaseModal.vue'

interface Props {
  modelValue: boolean
  title?: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'default'
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Confirm',
  message: 'Are you sure?',
  confirmLabel: 'Delete',
  cancelLabel: 'Cancel',
  variant: 'danger',
  loading: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
}>()

function handleCancel() {
  emit('update:modelValue', false)
  emit('cancel')
}

function handleConfirm() {
  if (props.loading) return
  emit('confirm')
}

function handleKeydown(e: KeyboardEvent) {
  if (!props.modelValue || props.loading) return

  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    handleConfirm()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    handleCancel()
  }
}

// Listen for keyboard events only while modal is open
watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleKeydown)
  } else {
    document.removeEventListener('keydown', handleKeydown)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <BaseModal
    :model-value="modelValue"
    :title="title"
    size="sm"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="confirm-message">{{ message }}</p>

    <template #footer>
      <button
        type="button"
        class="btn btn--cancel"
        :disabled="loading"
        @click="handleCancel"
      >
        {{ cancelLabel }}
      </button>
      <button
        type="button"
        :class="['btn', variant === 'danger' ? 'btn--danger' : 'btn--primary']"
        :disabled="loading"
        @click="handleConfirm"
      >
        <svg v-if="loading" class="btn-spinner" viewBox="0 0 24 24" fill="none">
          <circle class="btn-spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
          <path class="btn-spinner__head" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ confirmLabel }}
      </button>
    </template>
  </BaseModal>
</template>

<style scoped>
.confirm-message {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn--cancel {
  background: var(--bg-surface);
  border-color: var(--border-color);
  color: var(--text-secondary);
}

.btn--cancel:hover:not(:disabled) {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.btn--danger {
  background: var(--color-error);
  color: white;
}

.btn--danger:hover:not(:disabled) {
  opacity: 0.9;
}

.btn--primary {
  background: var(--color-primary);
  color: white;
}

.btn--primary:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  animation: spin 1s linear infinite;
}

.btn-spinner__track {
  opacity: 0.3;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
